import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import gameService from '../../../services/gameService';
import { AuthContext } from '../../../context/AuthContext';
import {
  Button,
  Card,
  Spinner,
  Alert,
  Row,
  Col,
  Modal,
  InputGroup,
  FormControl,
} from 'react-bootstrap';
import {
  FaCoins,
  FaRegGem,
  FaPlayCircle,
  FaCrown,
  FaInfoCircle,
  FaHistory,
  FaArrowLeft,
} from 'react-icons/fa';
import '../../../styles/casino-theme.css';

const WarGame = () => {
  const { auth } = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [betAmount, setBetAmount] = useState(1);
  const [playerCard, setPlayerCard] = useState(null);
  const [dealerCard, setDealerCard] = useState(null);
  const [gameResult, setGameResult] = useState('');
  const [newBalance, setNewBalance] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [showHistory, setShowHistory] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [history, setHistory] = useState([]);

  const getCardImage = (card) =>
    `${process.env.PUBLIC_URL}/images/games/cards/${card}.png`;

  const handlePlay = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await gameService.playWar(Number(betAmount));
      setPlayerCard(response.playerCard);
      setDealerCard(response.dealerCard);
      setGameResult(response.result);
      setNewBalance(response.newBalance);
    } catch (err) {
      setError(err?.response?.data?.error || t('war.error_play'));
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const fetchedHistory = await gameService.getWarHistory();
      setHistory(Array.isArray(fetchedHistory) ? fetchedHistory : []);
    } catch {
      setError(t('war.error_history'));
    } finally {
      setLoading(false);
    }
  };

  const handleShowHistory = async () => {
    await fetchHistory();
    setShowHistory(true);
  };

  const handleCloseHistory = () => setShowHistory(false);
  const handleShowRules = () => setShowRules(true);
  const handleCloseRules = () => setShowRules(false);

  const balanceToShow =
    typeof newBalance === 'number'
      ? newBalance
      : typeof auth?.balance === 'number'
      ? auth.balance
      : null;

  const resultColor =
    gameResult === 'win' ? 'success' : gameResult === 'lose' ? 'danger' : 'warning';

  return (
    <div className="pgp-container d-flex justify-content-center align-items-start">
      <Card className="pgp-card shadow-lg w-100" style={{ maxWidth: 1060 }}>
        <Card.Body>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Button
              variant="secondary"
              onClick={() => navigate('/sessions')}
              title={t('common.back', { defaultValue: 'Back' })}
            >
              <FaArrowLeft className="me-2" />
              {t('common.back', { defaultValue: 'Back' })}
            </Button>

            <h2 className="m-0">
              <FaCrown className="text-warning me-2" />
              {t('war.title')}
              <FaCrown className="text-warning ms-2" />
            </h2>

            <div className="d-flex gap-2">
              <Button variant="outline-info" onClick={handleShowHistory}>
                <FaHistory className="me-2" />
                {t('war.history')}
              </Button>
              <Button variant="outline-secondary" onClick={handleShowRules}>
                <FaInfoCircle className="me-2" />
                {t('war.rules')}
              </Button>
            </div>
          </div>

          {/* Toolbar: Apuesta / Jugar / Balance */}
          <div className="pgp-toolbar">
            <InputGroup style={{ maxWidth: 260 }}>
              <InputGroup.Text>
                <FaCoins className="me-1" />
                {t('war.bet')}
              </InputGroup.Text>
              <FormControl
                type="number"
                min={1}
                max={10000}
                value={betAmount}
                onChange={(e) => setBetAmount(Number(e.target.value))}
                disabled={loading}
              />
              <InputGroup.Text>{t('war.currency', { defaultValue: 'MENEL' })}</InputGroup.Text>
            </InputGroup>

            <div className="d-flex gap-2">
              <Button className="" onClick={handlePlay} disabled={loading}>
                {loading ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <FaPlayCircle className="me-2" />
                    {t('war.play')}
                  </>
                )}
              </Button>
            </div>

            <div className="pgp-balance">
              {t('war.balance')}: <strong>{balanceToShow ?? '—'}</strong>{' '}
              {t('war.currency', { defaultValue: 'MENEL' })}
            </div>
          </div>

          {/* Mensajes */}
          {error && (
            <Alert variant="danger" className="mt-3">
              {error}
            </Alert>
          )}

          {/* Descripción / ayuda corta */}
          <p className="text-muted pgp-mt-2 mb-0">{t('war.description')}</p>

          {/* Resultado y cartas */}
          {gameResult && (
            <div className="mt-3">
              <div className="d-flex align-items-center gap-2 mb-2">
                <span className={`badge bg-${resultColor}`}>
                  {gameResult === 'win'
                    ? t('war.result.win')
                    : gameResult === 'lose'
                    ? t('war.result.lose')
                    : t('war.result.tie')}
                </span>
              </div>

              <Row className="g-3">
                <Col md={6}>
                  <div className="pgp-zone">
                    <div className="pgp-zone-header">
                      {t('war.you', { defaultValue: 'You' })}
                    </div>
                    <div className="pgp-cards">
                      {playerCard && (
                        <img
                          src={getCardImage(playerCard.card)}
                          alt={playerCard.card}
                          className="pgp-card-img static"
                        />
                      )}
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="pgp-zone">
                    <div className="pgp-zone-header">
                      {t('war.dealer', { defaultValue: 'Dealer' })}
                    </div>
                    <div className="pgp-cards">
                      {dealerCard && (
                        <img
                          src={getCardImage(dealerCard.card)}
                          alt={dealerCard.card}
                          className="pgp-card-img static"
                        />
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal: Historial */}
      <Modal show={showHistory} onHide={handleCloseHistory} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaHistory className="me-2" />
            {t('war.history')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: 420, overflowY: 'auto' }}>
          {loading ? (
            <div className="d-flex justify-content-center py-4">
              <Spinner />
            </div>
          ) : history.length ? (
            <ul className="list-group">
              {history.map((entry, index) => (
                <li key={index} className="list-group-item">
                  <div>
                    <strong>{t('war.result.title', { defaultValue: 'Result' })}:</strong>{' '}
                    {entry.result}
                  </div>
                  <div>
                    <strong>{t('war.date', { defaultValue: 'Date' })}:</strong>{' '}
                    {new Date(entry.created_at).toLocaleString()}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-muted">
              {t('war.no_history', { defaultValue: 'No records yet.' })}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Modal: Reglas */}
      <Modal show={showRules} onHide={handleCloseRules} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaInfoCircle className="me-2" />
            {t('war.rules')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5 className="mb-2">{t('war.rules_title')}</h5>
          <p className="text-muted">{t('war.rules_description')}</p>
          <ul className="mb-0">
            <li>
              <strong>{t('war.rules_bet')}</strong>
            </li>
            <li>
              <strong>{t('war.rules_goal')}</strong>
            </li>
            <li>
              <strong>{t('war.rules_tie')}</strong>
            </li>
          </ul>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default WarGame;
